// Timestamp Photo stamp: a slim dark strip along the bottom of the photo with the time, date,
// coordinates and address (no employee name). Every size follows the photo width, so it takes the
// same share of the photo on any phone. The note is never drawn (only the admin sees it).

export const splitClock = (d) => {
  const parts = d
    .toLocaleTimeString("en-IN", { timeZone: "Asia/Kolkata", hour: "numeric", minute: "2-digit", second: "2-digit", hour12: true })
    .split(/[\s  ]+/);
  return { clock: parts[0] || "", ampm: (parts[1] || "").toUpperCase() };
};

export const fmtStampDate = (d) =>
  d.toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata", weekday: "short", day: "numeric", month: "short", year: "numeric" });

const FONT = "'Plus Jakarta Sans', -apple-system, 'Segoe UI', Arial, sans-serif";

// Wraps text into at most `maxLines` lines that fit `maxWidth`; the last line ends with "…" if cut.
function wrap(ctx, text, maxWidth, maxLines) {
  const words = String(text || "").split(/\s+/).filter(Boolean);
  const lines = [];
  let line = "";
  for (let i = 0; i < words.length; i++) {
    const test = line ? `${line} ${words[i]}` : words[i];
    if (ctx.measureText(test).width <= maxWidth) {
      line = test;
      continue;
    }
    if (lines.length === maxLines - 1) {
      let last = [line, ...words.slice(i)].join(" ");
      while (last.length > 1 && ctx.measureText(`${last}…`).width > maxWidth) last = last.slice(0, -1);
      lines.push(`${last.trim()}…`);
      return lines;
    }
    lines.push(line);
    line = words[i];
  }
  if (line) lines.push(line);
  return lines;
}

export function drawTimestamp(ctx, { width, height, date, location, address }) {
  const k = Math.max(0.75, Math.min(3, width / 380)); // 380px-wide photo is the reference size
  const z = (n) => n * k;
  const font = (weight, size) => `${weight} ${z(size)}px ${FONT}`;

  const padX = z(13);
  const padY = z(12);
  const { clock, ampm } = splitClock(date);
  const dateText = fmtStampDate(date);
  const coords = location ? `${location.latitude.toFixed(4)}, ${location.longitude.toFixed(4)}` : "";

  ctx.font = font("600", 13);
  const addrLines = address ? wrap(ctx, address, width - padX * 2, 3) : [];

  const addrH = addrLines.length ? addrLines.length * z(17) : 0;
  const coordsH = coords ? z(16) : 0;
  const gapH = z(9);
  const timeH = z(21);
  const boxH = padY * 2 + addrH + coordsH + gapH + timeH;
  const top = height - boxH;

  ctx.fillStyle = "rgba(6,12,24,0.70)";
  ctx.fillRect(0, top, width, boxH);
  ctx.fillStyle = "#22D3EE";
  ctx.fillRect(0, top, width, Math.max(2, z(2.5)));

  ctx.textBaseline = "alphabetic";
  let y = top + padY;

  // Address first, in detail: up to three lines
  if (addrLines.length) {
    ctx.textAlign = "left";
    ctx.fillStyle = "#E2E8F0";
    ctx.font = font("600", 13);
    addrLines.forEach((l, i) => {
      y += z(13);
      ctx.fillText(l, padX, y);
      y += z(4);
    });
  }

  // Coordinates, under the address
  if (coordsH) {
    y += z(11);
    ctx.textAlign = "left";
    ctx.fillStyle = "#CBD5E1";
    ctx.font = font("400", 11);
    ctx.fillText(coords, padX, y);
  }

  // Bottom line: time + AM/PM (left), date (right)
  const base1 = top + boxH - padY - z(5);
  ctx.textAlign = "left";
  ctx.fillStyle = "#FFFFFF";
  ctx.font = font("800", 17);
  ctx.fillText(clock, padX, base1);
  const clockW = ctx.measureText(clock).width;
  ctx.fillStyle = "#67E8F9";
  ctx.font = font("800", 10.5);
  ctx.fillText(ampm, padX + clockW + z(5), base1);

  ctx.textAlign = "right";
  ctx.fillStyle = "#E2E8F0";
  ctx.font = font("600", 12.5);
  ctx.fillText(dateText, width - padX, base1);
}
